import { 
  LayoutDashboard, 
  ClipboardList, 
  Sliders, 
  Zap, 
  Shield, 
  Star, 
  QrCode, 
  User, 
  Settings, 
  Users, 
  Package, 
  Calendar,
  CalendarCheck,
  DollarSign, 
  Download,
  Workflow,
  Handshake,
  PieChart,
  Tag,
  Bell,
  Store,
  CreditCard,
  Sparkles,
  TrendingUp,
  Receipt,
  FileText,
  UserPlus,
  Target,
  Share2,
  TicketCheck
} from "lucide-react";

/**
 * Navigation structure for DashboardV2
 * SINGLE SOURCE OF TRUTH for sidebar tabs
 * Used by: DashboardV2, RolesTab (for permission checkboxes)
 */
export const navigationGroups = [
  {
    id: 'home',
    label: 'Dashboard',
    icon: LayoutDashboard,
    isStandalone: true
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: Settings,
    items: [
      { id: 'roles', label: 'Roles', icon: Shield },
      { id: 'teamMembers', label: 'Team', icon: Users },
      { id: 'assets', label: 'Assets', icon: Package },
      { id: 'services', label: 'Services', icon: Zap },
      { id: 'mySchedule', label: 'Schedule', icon: CalendarCheck },
      { id: 'schedule', label: 'Bookings', icon: Calendar },
      { id: 'assignments', label: 'Assignments', icon: TicketCheck },
      { id: 'flows', label: 'Flows', icon: Sparkles },
    ]
  },
  {
    id: 'financials',
    label: 'Financials',
    icon: DollarSign,
    items: [
      { id: 'payments', label: 'Overview', icon: CreditCard },
      { id: 'profitLoss', label: 'P&L', icon: PieChart },
      { id: 'revenue', label: 'Revenue', icon: TrendingUp },
      { id: 'expenses', label: 'Expenses', icon: Receipt },
      { id: 'partners', label: 'Partners', icon: UserPlus },
    ]
  },
  {
    id: 'customers',
    label: 'Customers',
    icon: ClipboardList,
    items: [
      { id: 'branding', label: 'Branding', icon: Sliders },
      { id: 'waivers', label: 'Waivers', icon: Shield },
      { id: 'reviews', label: 'Reviews', icon: Star },
      { id: 'checkin', label: 'Check-In', icon: QrCode },
      { id: 'relations', label: 'Relations', icon: User },
      { id: 'discounts', label: 'Discounts', icon: Tag },
      { id: 'notifications', label: 'Notifications', icon: Bell },
    ]
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: Store,
    items: [
      { id: 'marketplace', label: 'Marketplace', icon: Store },
      { id: 'blog', label: 'SEO', icon: FileText },
      { id: 'advertising', label: 'Advertising', icon: Zap },
      { id: 'conversions', label: 'Conversions', icon: Target },
      { id: 'distribution', label: 'Distribution', icon: Share2 },
    ]
  }
];

/**
 * Get all tab IDs as flat array (for role permissions)
 */
export const getAllTabIds = () => {
  const tabs = [];
  
  navigationGroups.forEach(group => {
    if (group.isStandalone) {
      tabs.push({ id: group.id, label: group.label, group: null });
    } else if (group.items) {
      group.items.forEach(item => {
        tabs.push({ id: item.id, label: item.label, group: group.label });
      });
    }
  });
  
  return tabs;
};

/**
 * Filter navigation based on role permissions
 * @param {Object} tabPermissions - Object like { branding: { read: true, create: false }, waivers: { read: true } }
 * @param {Boolean} isAdmin - If true, show all tabs
 */
export const filterNavigationByPermissions = (tabPermissions = {}, isAdmin = false) => {
  if (isAdmin) {
    return navigationGroups; // Admins see everything
  }
  
  const filtered = navigationGroups.map(group => {
    if (group.isStandalone) {
      // Check if user has permission for standalone item (like 'home')
      const permission = tabPermissions[group.id];
      const hasAccess = permission?.read === true || permission === true;
      return hasAccess ? group : null;
    }
    
    // Filter items based on permissions - check if user has READ access
    const filteredItems = group.items.filter(item => {
      const permission = tabPermissions[item.id];
      // Permission can be an object { read: true, ... } or a boolean for legacy support
      let hasAccess = false;
      if (typeof permission === 'object') {
        hasAccess = permission?.read === true;
      } else {
        hasAccess = permission === true;
      }
      return hasAccess;
    });
    
    return {
      ...group,
      items: filteredItems
    };
  }).filter(group => {
    // Remove null groups (standalone without permission)
    if (group === null) return false;
    // Remove groups with no items
    if (group.items && group.items.length === 0) return false;
    return true;
  });
  
  return filtered;
};
